import { requeteRh } from './apercu';

/**
 * Toutes les requêtes du module passent par `requeteRh`, qui porte l'en-tête
 * d'aperçu. Utiliser le `request` générique ferait répondre le serveur avec la
 * session réelle : en bac à sable, on lirait ses propres soldes en croyant lire
 * ceux de la personne endossée.
 */
const request = requeteRh;

/**
 * Client API du module Congés.
 *
 * Séparé de `ndf-api.ts` comme celui-ci l'est de `api.ts`, mais réutilise le
 * même `request` : le traitement des erreurs et la redirection sur session
 * expirée ne doivent pas diverger d'un module à l'autre.
 *
 * La carte d'identité n'est PAS ici : elle reste servie par `ndfApi.moi()`, qui
 * porte les permissions de tous les modules RH. En appeler une seconde ferait
 * deux réponses à tenir d'accord.
 */

export interface CategorieAbsence {
  id: string;
  code: string;
  libelle: string;
  ordre: number;
  actif: boolean;
}

export interface Mecanisme {
  code: string;
  libelle: string;
  aide: string;
}

export type RestrictionEligibilite = 'aucune' | 'apprentis' | 'stagiaires' | 'forfait_jour';

export interface TypeAbsence {
  id: string;
  /** Dérivé du libellé côté serveur, puis figé. Affiché, jamais modifiable. */
  code: string;
  libelle: string;
  categorie_id: string | null;
  origine: 'collaborateur' | 'rh';
  visible_collaborateur: boolean;
  restriction_eligibilite: RestrictionEligibilite;
  /** JSON : tableau de codes du catalogue des mécanismes. */
  mecanismes: string;
  /** Plafond du « compteur annuel simple ». */
  plafond_annuel: number | null;
  /** Ce que le plafond veut dire pour le salarié : par an, ou par événement. */
  plafond_periode: 'an' | 'evenement';
  /** Ce que le « ? » rappelle en plus du barème (fenêtres, extensions légales). */
  aide_plafond: string | null;
  /** La phrase qui introduit le barème dans le « ? ». */
  bareme_intro: string | null;
  decompte: boolean;
  code_silae: string | null;
  ordre: number;
  actif: boolean;
}

/** Ce qu'un salarié peut demander, groupé par catégorie (route « proposables »). */
export interface CategorieProposee {
  categorie: CategorieAbsence;
  types: TypeAbsence[];
}

/** Une ligne de barème d'aide — informative, jamais contraignante (§B3 v1.9). */
export interface BaremeEvenement {
  id: string;
  type_absence_id: string;
  libelle: string;
  nb_jours: number;
  precisions: string | null;
  ordre: number;
  actif: boolean;
}

/** Un palier du barème d'ancienneté Syntec (R-CPT-13). */
export interface PalierAnciennete {
  id?: string;
  annees: number;
  jours: number;
}

export interface LigneSimulationAnciennete {
  salarie_id: string;
  nom: string;
  date_entree: string | null;
  annees: number;
  jours: number;
  deja_credite: boolean;
}

/** Une ligne du suivi des justificatifs différés (R-ABS-07). */
export interface LigneJustificatifSuivi {
  demandeId: string;
  salarie: string;
  type: string;
  debut: string;
  fin: string;
  debut_demi: string;
  fin_demi: string;
  statut: 'attendu' | 'depose' | 'valide' | 'refuse';
  depose_at: string | null;
  relance_at: string | null;
  motif: string | null;
  pieces: number;
}

export interface ParametreConges {
  key: string;
  value: string | null;
}

/** Le corps envoyé au serveur : les mécanismes y sont un TABLEAU, pas du JSON. */
export type TypeAbsenceEnvoi = Partial<Omit<TypeAbsence, 'id' | 'code' | 'mecanismes'>> & {
  mecanismes?: string[];
};

/** Les trois lectures d'un compteur (R-CPT-11). */
export interface LectureCompteur {
  cle: string;
  libelle: string;
  courant: number;
  en_attente: number;
  previsionnel: number;
}

/** Un type à plafond annuel : pas un solde, un droit consommé. */
export interface LecturePlafond {
  type_id: string;
  libelle: string;
  plafond: number;
  /** 'an' | 'mois' | 'stage' : ce que le plafond couvre. */
  plafond_periode: string;
  non_remunere: boolean;
  pris: number;
  en_attente: number;
  restant: number;
}

export interface AbsenceLigne {
  id: string;
  type_absence_id: string;
  type_libelle: string;
  date_debut: string;
  date_fin: string;
  debut_demi: string;
  fin_demi: string;
  nb_jours: number;
  statut: string;
  origine: string;
  description: string | null;
  commentaire: string | null;
}

export interface MouvementCompteur {
  id: string;
  compteur: string;
  date_effet: string;
  jours: number;
  origine: string;
  commentaire: string | null;
}

export interface MesConges {
  salarie: { id: string; prenom: string; nom: string };
  compteurs: LectureCompteur[];
  plafonds: LecturePlafond[];
  plafond_absence_autorisee: number | null;
  absences: AbsenceLigne[];
  mouvements: MouvementCompteur[];
}

// ─── Reprise 2026 (administration) ──────────────────────────────────────────

export interface ControleReprise {
  salarie_id: string;
  reconstruit_cp: number;
  reconstruit_rtt: number;
  reference_cp: number | null;
  reference_rtt: number | null;
  ecart_cp: number | null;
  ecart_rtt: number | null;
}

/** D'où vient chaque solde — sert à localiser la colonne fautive (R-CPT-12). */
export interface DecompositionSoldes {
  report: number;
  acquis: number;
  anciennete: number;
  absences: number;
  autres: number;
  cpNet: number;
  rttCredit: number;
  rttPris: number;
  rttNet: number;
}

export interface SalarieReprise {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  forfait_jour: boolean;
  /** La fiche ouvre-t-elle des compteurs, et sinon pourquoi (R-SAL-13). */
  compteurs_ouverts: boolean;
  motif_exclusion: string | null;
  report: number | null;
  soldes: Record<string, number> | null;
  absences_importees: number;
  controle: ControleReprise | null;
  decomposition: DecompositionSoldes | null;
}

export interface EtatReprise {
  arrete_au: string | null;
  total_importees: number;
  salaries: SalarieReprise[];
}

export interface LigneAnalysee {
  numero: number;
  email: string;
  typeAirtable: string;
  debut: string | null;
  fin: string | null;
  debutDemi: string;
  finDemi: string;
  joursAirtable: number | null;
  joursCalcules: number;
  description: string | null;
  commentaire: string | null;
  anomalies: string[];
  salarie_id: string | null;
  salarie_nom: string | null;
  type_gertrude_id: string | null;
  bloquante: boolean;
}

export interface AnalyseImport {
  erreurs: string[];
  types_airtable: string[];
  mapping_propose: Record<string, string | null>;
  types_gertrude: Array<{ id: string; libelle: string }>;
  lignes: LigneAnalysee[];
  nb_bloquantes: number;
  nb_signalees: number;
}

// ─── Demandes d'absence (Lot 3, specs §D) ───────────────────────────────────

export interface DecoupageDedoublement {
  joursAcquis: number;
  joursSansSolde: number;
  cp: { debut: string; fin: string; debutDemi: string; finDemi: string };
  sansSolde: { debut: string; fin: string; debutDemi: string; finDemi: string };
}

export interface VerdictSoumission {
  blocages: string[];
  alertes: string[];
  nbJours: number;
  /** Le compteur consommé, ou null : rien n'est décompté (congé sans solde). */
  compteur: string | null;
  nonRemunere: boolean;
  anticipation?: { soldeProjete: number; manque: number };
  aPosteriori: boolean;
  dedoublement: DecoupageDedoublement | null;
}

export interface DemandeAbsence {
  /** Justificatif différé : null quand l'absence ne demande aucune pièce. */
  justificatif_statut?: 'attendu' | 'depose' | 'valide' | 'refuse' | null;
  justificatif_motif?: string | null;
  /** Compteur consommé, déduit des mécanismes. `null` : aucun. */
  compteur?: string | null;
  id: string;
  salarie_id: string;
  type_absence_id: string;
  type_libelle?: string | null;
  date_debut: string;
  date_fin: string;
  debut_demi: string;
  fin_demi: string;
  nb_jours: number;
  statut: string;
  origine: string;
  description: string | null;
  commentaire: string | null;
  motif_decision: string | null;
  a_posteriori: boolean;
  a_regulariser_paie: boolean;
  demande_liee_id: string | null;
  soumise_at: string | null;
  decidee_at: string | null;
  decidee_par: string | null;
}

export interface EvenementChronologie {
  id: string;
  type_evenement: string;
  acteur_email: string | null;
  details: string | null;
  created_at: string;
}

export interface DetailDemande {
  demande: DemandeAbsence;
  roles: string[];
  chronologie: EvenementChronologie[];
  liee: DemandeAbsence | null;
  justificatifs: Array<{ id: string; filename: string }>;
}

export interface DemandeAValider {
  id: string;
  salarie_id: string;
  salarie_nom: string;
  type_libelle: string;
  date_debut: string;
  date_fin: string;
  debut_demi: string;
  fin_demi: string;
  nb_jours: number;
  statut: string;
  a_posteriori: boolean;
  demande_liee_id: string | null;
  description: string | null;
  soumise_at: string | null;
}

/** Tout ce qu'il faut pour trancher, sur un seul écran (R-VAL-10). */
export interface LigneRegistre extends DemandeAValider {
  type_absence_id: string;
  origine: string;
  a_regulariser_paie: boolean;
  motif_decision: string | null;
  decidee_at: string | null;
}

export interface FiltresRegistre {
  salarie_id?: string;
  type_id?: string;
  statut?: string;
  debut?: string;
  fin?: string;
  a_regulariser?: boolean;
}

export interface DossierValidation {
  demande: DemandeAbsence;
  salarie: { id: string; prenom: string; nom: string } | null;
  type: TypeAbsence | null;
  compteur: string | null;
  soldes: {
    avant: Record<string, number>;
    apres: Record<string, number>;
    enAttente: Record<string, number>;
  };
  equipe: Array<{
    salarie: string;
    type_libelle: string;
    date_debut: string;
    date_fin: string;
    statut: string;
  }>;
  alertes: string[];
  roles: string[];
}

export interface MouvementSalarie {
  id: string;
  compteur: string;
  date_effet: string;
  jours: number;
  origine: string;
  commentaire: string | null;
  auteur_email: string | null;
}

export interface AbsenceCalendrier {
  id: string;
  type_libelle: string;
  date_debut: string;
  date_fin: string;
  debut_demi: string;
  fin_demi: string;
  nb_jours: number;
  statut: string;
  origine: string;
  a_valider_par_moi: boolean;
}

export interface Calendrier {
  debut: string;
  fin: string;
  feries: string[];
  perimetre_complet: boolean;
  lignes: Array<{ salarie_id: string; nom: string; absences: AbsenceCalendrier[] }>;
}

// ─── Absences saisies par la RH (Lot 6, specs §G) ───────────────────────────

export interface ArretTravail {
  id: string;
  salarie_id: string;
  demande_id: string | null;
  type_arret: string;
  date_debut: string;
  date_fin: string;
  arret_initial_id: string | null;
  commentaire: string | null;
  cree_par: string | null;
  /** 'actif' ou 'annule' : un arrêt annulé reste lisible (R-AT-06). */
  statut?: string;
  annule_le?: string | null;
  annule_par?: string | null;
  motif_annulation?: string | null;
}

export interface EpisodeArret {
  initial: ArretTravail;
  prolongations: ArretTravail[];
  debut: string;
  fin: string;
  attestations: Array<{ id: string; filename: string; arret_travail_id: string | null }>;
}

export type TravailDuJour = 'journee' | 'demi' | 'non';

export interface JourDeRythme {
  jour: 'lundi' | 'mardi' | 'mercredi' | 'jeudi' | 'vendredi';
  travail: TravailDuJour;
  pauseCouverte: boolean;
}

export interface PeriodeMiTemps {
  id: string;
  salarie_id: string;
  arret_travail_id: string | null;
  date_debut: string;
  date_fin: string;
  rythme: JourDeRythme[];
  commentaire: string | null;
  exceptions: Array<{
    id: string;
    date: string;
    travail: TravailDuJour;
    pause_couverte: boolean;
    motif: string | null;
  }>;
}

export interface CorpsDemande {
  type_absence_id: string;
  date_debut: string;
  date_fin: string;
  debut_demi: string;
  fin_demi: string;
  description?: string | null;
  commentaire?: string | null;
}

export const congesApi = {
  mesConges: () => request<MesConges>('/api/conges/mes-conges'),
  demandes: {
    liste: () => request<{ demandes: DemandeAbsence[] }>('/api/conges/demandes'),
    detail: (id: string) => request<DetailDemande>(`/api/conges/demandes/${id}`),
    verifier: (corps: CorpsDemande & { a_justificatif?: boolean }) =>
      request<VerdictSoumission>('/api/conges/demandes/verifier', {
        method: 'POST',
        body: JSON.stringify(corps),
      }),
    creer: (
      corps: CorpsDemande & {
        justificatif?: { filename: string; contenu_base64: string; mime?: string | null };
      },
    ) =>
      request<{ id: string; alertes: string[] }>('/api/conges/demandes', {
        method: 'POST',
        body: JSON.stringify(corps),
      }),
    dedoubler: (corps: CorpsDemande) =>
      request<{ demandes: string[]; decoupage: DecoupageDedoublement }>(
        '/api/conges/demandes/dedoublement',
        { method: 'POST', body: JSON.stringify(corps) },
      ),
    aValider: () => request<{ demandes: DemandeAValider[] }>('/api/conges/demandes/a-valider/liste'),
    registre: (f: FiltresRegistre = {}) => {
      const q = new URLSearchParams();
      for (const [cle, valeur] of Object.entries(f)) {
        if (valeur !== undefined && valeur !== '' && valeur !== false) q.set(cle, String(valeur));
      }
      const suffixe = q.toString();
      return request<{ demandes: LigneRegistre[] }>(
        `/api/conges/demandes/registre/liste${suffixe ? `?${suffixe}` : ''}`,
      );
    },
    calendrier: (debut: string, fin: string) =>
      request<Calendrier>(`/api/conges/demandes/calendrier/periode?debut=${debut}&fin=${fin}`),
    dossier: (id: string) => request<DossierValidation>(`/api/conges/demandes/${id}/dossier`),
    justificatif: (id: string, pieceId: string) =>
      request<{ filename: string; mime: string | null; contenu_base64: string }>(
        `/api/conges/demandes/${id}/justificatif/${pieceId}`,
      ),
    /**
     * Justificatif différé — le justificatif différé.
     *
     * `suivi` est la liste de la RH : ce qu'elle doit vérifier d'abord, ce qui
     * manque encore ensuite. `deposer` est le chemin qui n'existait pas — la
     * pièce arrive après l'événement, l'acte de décès étant remis le jour des
     * obsèques.
     */
    justificatifsASuivre: () =>
      request<{ justificatifs: LigneJustificatifSuivi[] }>('/api/conges/demandes/justificatifs'),
    deposerJustificatif: (id: string, fichier: { filename: string; contenu_base64: string; mime?: string | null }) =>
      request<{ ok: true }>(`/api/conges/demandes/${id}/justificatif`, {
        method: 'POST',
        body: JSON.stringify(fichier),
      }),
    deciderJustificatif: (id: string, decision: { valide: boolean; motif?: string | null }) =>
      request<{ ok: true }>(`/api/conges/demandes/${id}/justificatif/decision`, {
        method: 'POST',
        body: JSON.stringify(decision),
      }),
    /** R-CPT-14 : tous les bénéficiaires, en trois lectures. */
    tousLesCompteurs: () =>
      request<{
        salaries: Array<{
          salarie_id: string;
          nom: string;
          /**
           * Renseigné quand la fiche ne peut pas porter de compteurs (pas de
           * date d'entrée, par exemple). Sans lui, la RH lit « 0,00 j » et
           * comprend « cette personne n'a plus de congés » au lieu de « cette
           * fiche est incomplète ».
           */
          motif_exclusion: string | null;
          compteurs: LectureCompteur[];
        }>;
      }>('/api/conges/demandes/compteurs'),
    compteurs: (salarieId: string) =>
      request<{
        salarie: { id: string; prenom: string; nom: string };
        soldes: Record<string, number>;
        mouvements: MouvementSalarie[];
      }>(`/api/conges/demandes/compteurs/${salarieId}`),
    corriger: (salarieId: string, corps: { compteur: string; jours: number; commentaire: string }) =>
      request<{ soldes: Record<string, number> }>(
        `/api/conges/demandes/compteurs/${salarieId}/corriger`,
        { method: 'POST', body: JSON.stringify(corps) },
      ),
    transition: (id: string, transition: string, motif?: string | null) =>
      request<{ statut: string }>(`/api/conges/demandes/${id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ transition, motif: motif ?? null }),
      }),
  },
  reprise: {
    etat: () => request<EtatReprise>('/api/conges/reprise/etat'),
    report: (salarie_id: string, jours: number) =>
      request<{ ok: boolean }>('/api/conges/reprise/report', {
        method: 'POST',
        body: JSON.stringify({ salarie_id, jours }),
      }),
    references: (arrete_au: string, references: Array<{ salarie_id: string; cp: number; rtt: number }>) =>
      request<{ ok: boolean }>('/api/conges/reprise/references', {
        method: 'POST',
        body: JSON.stringify({ arrete_au, references }),
      }),
    reconstruire: () =>
      request<{ bascule: number; acquisition: number }>('/api/conges/reprise/reconstruire', {
        method: 'POST',
      }),
    analyser: (contenu: string) =>
      request<AnalyseImport>('/api/conges/reprise/import/analyse', {
        method: 'POST',
        body: JSON.stringify({ contenu }),
      }),
    executer: (contenu: string, mapping: Record<string, string>) =>
      request<{ importees: number; deja: number; total: number }>('/api/conges/reprise/import/executer', {
        method: 'POST',
        body: JSON.stringify({ contenu, mapping }),
      }),
    // Portée obligatoire (R-SOC-12) : le serveur refuse un appel sans l'une ni l'autre.
    annulerImport: (portee: 'tous' | { salarieId: string }) =>
      request<{ demandes: number; mouvements: number }>(
        `/api/conges/reprise/import/annuler?${
          portee === 'tous' ? 'tous=1' : `salarie_id=${encodeURIComponent(portee.salarieId)}`
        }`,
        { method: 'POST' },
      ),
  },
  tickets: {
    mois: (mois: string) =>
      request<{
        mois: string;
        statut: string | null;
        total: number;
        /** Borne basse du calcul. `null` : aucun calcul n'est possible. */
        premier_mois: string | null;
        incompletes: Array<{ nom: string; manques: string[] }>;
        /**
         * Les demandes encore en attente qui chevauchent le mois. Elles
         * n'entrent PAS dans le calcul (les faits seulement) : l'écran les
         * montre pour qu'on relance le valideur avant de commander.
         */
        en_attente: Array<{
          id: string;
          salarie: string;
          type_libelle: string;
          date_debut: string;
          date_fin: string;
          nb_jours: number;
          statut: string;
        }>;
        lignes: Array<{
          id: string;
          salarie_id: string;
          jours_ouvres: number;
          jours_droit: number;
          regularisation: number;
          solde_negatif_reporte: number;
          solde_negatif_restant: number;
          tickets: number;
          statut: string;
          detail: {
            deductions: Array<{ jour: string; cause: string }>;
            regularisations: Array<{ jour: string; cause: string }>;
          };
          salarie: { id: string; prenom: string; nom: string };
        }>;
      }>(`/api/conges/tickets/${mois}`),
    calculer: (mois: string) =>
      request<{ lignes: number; refus: string | null }>(`/api/conges/tickets/${mois}/calculer`, {
        method: 'POST',
      }),
    valider: (mois: string) =>
      request<{ lignes: number }>(`/api/conges/tickets/${mois}/valider`, { method: 'POST' }),
    lienSwile: (mois: string) => `/api/conges/tickets/${mois}/swile.csv`,
    primes: (mois: string) =>
      request<{ primes: Array<{ id: string; salarie_nom: string; libelle: string; montant: number; commentaire: string | null }> }>(
        `/api/conges/tickets/primes/${mois}`,
      ),
    creerPrime: (corps: {
      salarie_id: string;
      mois: string;
      libelle: string;
      montant: number;
      commentaire?: string | null;
    }) => request<{ id: string }>('/api/conges/tickets/primes', { method: 'POST', body: JSON.stringify(corps) }),
    supprimerPrime: (id: string) =>
      request<{ ok: boolean }>(`/api/conges/tickets/primes/${id}`, { method: 'DELETE' }),
    paie: (mois: string) =>
      request<{
        mois: string;
        salaries: Array<{
          id: string;
          nom: string;
          tickets: number;
          absences: Array<{
            id: string;
            type_libelle: string;
            date_debut: string;
            date_fin: string;
            debut_demi: string;
            fin_demi: string;
            nb_jours: number;
            /** Jours décomptés qui tombent DANS le mois affiché (R-PAI-03). */
            jours_du_mois: number;
            dates_du_mois: string[];
            a_cheval: boolean;
            a_regulariser_paie: boolean;
            a_posteriori: boolean;
          }>;
          primes: Array<{ id: string; libelle: string; montant: number }>;
        }>;
      }>(`/api/conges/tickets/paie/${mois}`),
    regularise: (demandeId: string) =>
      request<{ ok: boolean }>(`/api/conges/tickets/paie/${demandeId}/regularise`, { method: 'POST' }),
  },
  absencesRh: {
    tousLesArrets: () =>
      request<{ episodes: Array<EpisodeArret & { salarie: { id: string; prenom: string; nom: string } }> }>(
        '/api/conges/absences-rh/arrets',
      ),
    arrets: (salarieId: string) =>
      request<{ episodes: EpisodeArret[] }>(`/api/conges/absences-rh/arrets/${salarieId}`),
    declarer: (corps: {
      salarie_id: string;
      type_arret: string;
      date_debut: string;
      date_fin: string;
      arret_initial_id?: string | null;
      commentaire?: string | null;
      document?: { filename: string; contenu_base64: string; mime?: string | null };
      /** R-AT-06 : réponse à la garde de vraisemblance au-delà d'un an. */
      confirme_duree?: boolean;
    }) =>
      request<{ arret: ArretTravail }>('/api/conges/absences-rh/arrets', {
        method: 'POST',
        body: JSON.stringify(corps),
      }),
    attestation: (
      arretId: string,
      piece: { filename: string; contenu_base64: string; mime?: string | null },
    ) =>
      request<{ id: string }>(`/api/conges/absences-rh/arrets/${arretId}/attestations`, {
        method: 'POST',
        body: JSON.stringify(piece),
      }),
    document: (pieceId: string) =>
      request<{ filename: string; mime: string | null; contenu_base64: string }>(
        `/api/conges/absences-rh/documents/${pieceId}`,
      ),
    /** Les types d'arrêt du référentiel (catégorie Santé, hors mi-temps). */
    typesArret: () =>
      request<{ types: Array<{ id: string; libelle: string }> }>(
        '/api/conges/absences-rh/types-arret',
      ),
    /** R-AT-06 : le détail d'un arrêt, pour le panneau d'actions. */
    arret: (id: string) =>
      request<{
        arret: ArretTravail;
        pieces: Array<{ id: string; filename: string }>;
        prolongations: ArretTravail[];
      }>(`/api/conges/absences-rh/arrets/detail/${id}`),
    modifierArret: (
      id: string,
      corps: {
        type_arret?: string;
        date_debut?: string;
        date_fin?: string;
        commentaire?: string | null;
        confirme_duree?: boolean;
      },
    ) =>
      request<{ arret: ArretTravail }>(`/api/conges/absences-rh/arrets/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(corps),
      }),
    annulerArret: (id: string, motif: string) =>
      request<{ ok: true }>(`/api/conges/absences-rh/arrets/${id}/annuler`, {
        method: 'POST',
        body: JSON.stringify({ motif }),
      }),
    miTemps: (salarieId: string) =>
      request<{ periodes: PeriodeMiTemps[] }>(`/api/conges/absences-rh/mi-temps/${salarieId}`),
    creerMiTemps: (corps: {
      salarie_id: string;
      arret_travail_id?: string | null;
      date_debut: string;
      date_fin: string;
      rythme: JourDeRythme[];
      commentaire?: string | null;
    }) =>
      request<{ id: string }>('/api/conges/absences-rh/mi-temps', {
        method: 'POST',
        body: JSON.stringify(corps),
      }),
    exception: (
      miTempsId: string,
      corps: { date: string; travail: TravailDuJour; pause_couverte: boolean; motif?: string | null },
    ) =>
      request<{ id: string }>(`/api/conges/absences-rh/mi-temps/${miTempsId}/exceptions`, {
        method: 'POST',
        body: JSON.stringify(corps),
      }),
  },
  categories: {
    liste: () => request<{ categories: CategorieAbsence[] }>('/api/conges/referentiels/categories'),
    modifier: (id: string, body: { libelle?: string; ordre?: number }) =>
      request<{ categorie: CategorieAbsence }>(`/api/conges/referentiels/categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  mecanismes: {
    liste: () => request<{ mecanismes: Mecanisme[] }>('/api/conges/referentiels/mecanismes'),
  },
  typesAbsence: {
    liste: () => request<{ types: TypeAbsence[] }>('/api/conges/referentiels/types-absence'),
    proposables: () =>
      request<{ categories: CategorieProposee[] }>('/api/conges/referentiels/types-absence/proposables'),
    creer: (body: TypeAbsenceEnvoi) =>
      request<{ type: TypeAbsence }>('/api/conges/referentiels/types-absence', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: TypeAbsenceEnvoi) =>
      request<{ type: TypeAbsence }>(`/api/conges/referentiels/types-absence/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  baremes: {
    liste: (typeAbsenceId?: string) =>
      request<{ baremes: BaremeEvenement[] }>(
        `/api/conges/referentiels/baremes${typeAbsenceId ? `?type_absence_id=${typeAbsenceId}` : ''}`,
      ),
    creer: (body: Partial<BaremeEvenement>) =>
      request<{ bareme: BaremeEvenement }>('/api/conges/referentiels/baremes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    modifier: (id: string, body: Partial<BaremeEvenement>) =>
      request<{ bareme: BaremeEvenement }>(`/api/conges/referentiels/baremes/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
  },
  anciennete: {
    bareme: () =>
      request<{ paliers: PalierAnciennete[] }>('/api/conges/referentiels/bareme-anciennete'),
    enregistrerBareme: (paliers: Array<{ annees: number; jours: number }>) =>
      request<{ paliers: PalierAnciennete[] }>('/api/conges/referentiels/bareme-anciennete', {
        method: 'PUT',
        body: JSON.stringify({ paliers }),
      }),
    simulation: (annee: number) =>
      request<{ annee: number; lignes: LigneSimulationAnciennete[] }>(
        `/api/conges/referentiels/anciennete/simulation?annee=${annee}`,
      ),
  },
  parametres: {
    liste: () => request<{ parametres: ParametreConges[] }>('/api/conges/referentiels/parametres'),
    enregistrer: (cle: string, valeur: string) =>
      request<{ ok: boolean }>('/api/conges/referentiels/parametres', {
        method: 'PUT',
        body: JSON.stringify({ cle, valeur }),
      }),
  },
};
